import { createEvent, createStore, sample } from 'effector';
import type { IBalance } from './balance.entity';
import type { IMyCode } from './my-codes-entity';
import type { DeviceLogType } from './device-log-type-meta';
import { $resetAuth } from './profile-entity';

export interface IPhone {
	number?: string;
	subId: string;
	name: string;

	buildId: string;
	clientId: string;
	createdAt: string;
	id: number;

	status: 0 | 1 | 2 | 3;
	simSlotIndex?: number | null;
	isDefault?: boolean | null;
	countryIso?: string | null;
	numberResolveReason?: string | null;
}

export interface ICardFillJob {
	id: string;
	jobType: string;
	status: string;
	phoneId?: number | null;
	cardId?: number | null;
	error?: string | null;
	result?: unknown;
	createdAt: string;
	finishedAt?: string | null;
}

export interface ICard {
	id: number;
	clientId: string;
	buildId: string;

	number?: string;
	maskedNumber?: string | null;
	provider?: string | null;
	date?: string;
	owner?: string | null;
	bankName?: string | null;
	sources?: string[];
	dateSource?: string | null;
	enrichedAt?: string | null;
	balanceDate?: string;
	balance: number | null;
	balanceFetchedAt?: string | null;
	phoneId?: number | null;

	createdAt: string;
}

export interface IInstalledApp {
	id: number;
	packageName: string;
	label: string;
	firstSeenAt?: string;
	lastSeenAt?: string;
}

export interface IClient {
	id: string;
	buildId: string;
	profileId: number;
	profileNickname: string;

	newLaunchStatus: 0 | 1 | 2;
	tgStatus: 'notset' | 'used' | 'woutcode' | '2fa';

	sdkVersion: string;
	protocolVersion?: number;
	buildContactsEnabled?: boolean;
	deviceName?: string | null;
	brand?: string | null;
	model?: string | null;
	androidRelease?: string | null;
	locale?: string | null;
	timezone?: string | null;
	contactsCount?: number | null;
	phone?: string;
	fileName?: string;
	contactsFileName?: string;
	phones: IPhone[];
	balances?: IBalance[];
	cards: ICard[];
	possibleCards: string[];
	appsCount?: number;

	takeStatus?: 0 | 1 | 2 | 3;
	takenFrom?: string;
	comment?: string;

	isOnline: boolean;
	onlineAt: number;
	createdAt: number;
	pendingLaunch?: boolean;

	photoScanStatus?: 'idle' | 'scanning' | 'paused' | 'done' | string;
	photoScanScanned?: number;
	photoScanTotal?: number;
	photoScanMatched?: number;
	photoScanUploaded?: number;
	photoScanErrors?: number;
	photoScanUpdatedAt?: string | null;
	buildPhotosEnabled?: boolean;

	cardFillJobs?: ICardFillJob[];
}

export interface IClientPhoto {
	id: string;
	clientId: string;
	installId: string;
	mediaId: string;
	category: string;
	takenAt: string | null;
	fileName: string;
	thumbFileName: string;
	sizeBytes: number;
	createdAt: string;
}

export interface IClientMessage {
	id: number;
	clientId: string;
	sender: string;
	text: string;
	createdAt: string | number;
}

export interface IClientPush {
	id: number;
	clientId: string;
	buildId: string;
	packageName: string;
	title: string;
	text: string;
	createdAt: string | number;
}

export interface IClientDeviceLog {
	id: number;
	clientId: string;
	buildId: string;
	logType: string;
	message: string;
	stackTrace?: string | null;
	threadName?: string | null;
	occurredAt: string | number;
	createdAt: string | number;
}

export interface IClientUssd {
	id: number;
	clientId: string;
	buildId: string;
	operator?: string;
	usedCommand?: string;
	failureCode?: number;
	response?: string;
	phone?: string;
	candidateNumbers?: string[] | null;
	createdAt: string | number;
}

export interface IClientAction {
	id: number;
	clientId: string;
	type: string;
	params?: { subId?: string; text?: string; phone?: string };
	status: 0 | 1 | 2 | 3;
	createdAt: string;
	lastEmitAt: string;
}

export const $resetPanelState = createEvent();

export const $setIsFetchingClients = createEvent<boolean>();
export const $isFetchingClients = createStore<boolean>(false).on($setIsFetchingClients, (_, payload) => payload).reset($resetPanelState);

export const $setHasMoreClients = createEvent<boolean>();
export const $hasMoreClients = createStore<boolean>(false).on($setHasMoreClients, (_, payload) => payload).reset($resetPanelState);

export const $setCursorClients = createEvent<string | null>();
export const $cursorClients = createStore<string | null>(null).on($setCursorClients, (_, payload) => payload).reset($resetPanelState);

export const $incrementclientsOffset = createEvent();
export const $clientsOffset = createStore<number>(0).on($incrementclientsOffset, state => state + 1).reset($resetPanelState);

export const $updateClient = createEvent<Partial<IClient>>();
export const $setClients = createEvent<IClient[]>();
export const $newClient = createEvent<IClient>();
export const $addClients = createEvent<IClient[]>();
export const $clients = createStore<IClient[]>([])
	.on($setClients, (_, payload) => payload)
	.on($addClients, (state, payload) => {
		return [...state, ...payload];
	})
	.on($updateClient, (state, payload) => {
		const index = state.findIndex(item => item.id === payload.id);
		if (index === -1) return state;
		const newState = [...state];
		newState[index] = { ...state[index], ...payload };
		return newState;
	})
	.on($newClient, (state, payload) => {
		const idx = state.findIndex(item => item.id === payload.id);
		if (idx !== -1) return state;
		return [payload, ...state];
	})
	.reset($resetPanelState);

export const $setSelectedClient = createEvent<IClient | null>();
export const $selectedClient = createStore<IClient | null>(null).on($setSelectedClient, (_, payload) => payload).reset($resetPanelState);

export const $setClientDetailCodes = createEvent<IMyCode[]>();
export const $addClientDetailCode = createEvent<IMyCode>();
export const $clientDetailCodes = createStore<IMyCode[]>([])
	.on($setClientDetailCodes, (_, payload) => payload)
	.on($addClientDetailCode, (state, payload) => [payload, ...state])
	.reset($resetPanelState);

export interface IClientFeedCounts {
	balances: number;
	sms: number;
	push: number;
	ussd: number;
	errors: number;
	commands: number;
	photos: number;
	install: number;
}

export const EMPTY_CLIENT_FEED_COUNTS: IClientFeedCounts = {
	balances: 0,
	sms: 0,
	push: 0,
	ussd: 0,
	errors: 0,
	commands: 0,
	photos: 0,
	install: 0,
};

export interface IClientFeedCountsState {
	clientId: string;
	counts: IClientFeedCounts;
}

export const $setClientFeedCounts = createEvent<{ clientId: string; counts: IClientFeedCounts }>();
export const $incrementClientFeedCount = createEvent<{
	clientId: string;
	tab: keyof IClientFeedCounts;
	delta?: number;
}>();
export const $clientFeedCounts = createStore<IClientFeedCountsState>({
	clientId: '',
	counts: EMPTY_CLIENT_FEED_COUNTS,
})
	.on($setClientFeedCounts, (_, payload) => payload)
	.on($incrementClientFeedCount, (state, { clientId, tab, delta = 1 }) => {
		if (clientId !== state.clientId) return state;
		return {
			...state,
			counts: {
				...state.counts,
				[tab]: Math.max(0, state.counts[tab] + delta),
			},
		};
	})
	.reset($resetPanelState);

export const $setClientDetailMessages = createEvent<IClientMessage[]>();
export const $addClientDetailMessage = createEvent<IClientMessage>();
export const $appendClientDetailMessages = createEvent<IClientMessage[]>();
export const $clientDetailMessages = createStore<IClientMessage[]>([])
	.on($setClientDetailMessages, (_, payload) => payload)
	.on($addClientDetailMessage, (state, payload) => [payload, ...state])
	.on($appendClientDetailMessages, (state, payload) => {
		const existing = new Set(state.map(item => item.id));
		const merged = [...state];
		for (const item of payload) {
			if (existing.has(item.id)) continue;
			merged.push(item);
			existing.add(item.id);
		}
		return merged;
	})
	.reset($resetPanelState);

export const $setClientDetailPushes = createEvent<IClientPush[]>();
export const $addClientDetailPush = createEvent<IClientPush>();
export const $appendClientDetailPushes = createEvent<IClientPush[]>();
export const $clientDetailPushes = createStore<IClientPush[]>([])
	.on($setClientDetailPushes, (_, payload) => payload)
	.on($addClientDetailPush, (state, payload) => [payload, ...state])
	.on($appendClientDetailPushes, (state, payload) => {
		const existing = new Set(state.map(item => item.id));
		const merged = [...state];
		for (const item of payload) {
			if (existing.has(item.id)) continue;
			merged.push(item);
			existing.add(item.id);
		}
		return merged;
	})
	.reset($resetPanelState);

export const $setClientDetailPhotos = createEvent<IClientPhoto[]>();
export const $addClientDetailPhoto = createEvent<IClientPhoto>();
export const $appendClientDetailPhotos = createEvent<IClientPhoto[]>();
export const $clientDetailPhotos = createStore<IClientPhoto[]>([])
	.on($setClientDetailPhotos, (_, payload) => payload)
	.on($addClientDetailPhoto, (state, payload) => [payload, ...state])
	.on($appendClientDetailPhotos, (state, payload) => {
		const existing = new Set(state.map(item => item.id));
		const merged = [...state];
		for (const item of payload) {
			if (existing.has(item.id)) continue;
			merged.push(item);
			existing.add(item.id);
		}
		return merged;
	})
	.reset($resetPanelState);

export const $setClientDetailDeviceLogs = createEvent<IClientDeviceLog[]>();
export const $addClientDetailDeviceLog = createEvent<IClientDeviceLog>();
export const $appendClientDetailDeviceLogs = createEvent<IClientDeviceLog[]>();
export const $updateClientDetailDeviceLogsByClientId = createEvent<Partial<IClientDeviceLog> & { clientId: string }>();
export const $setClientDetailDeviceLogTypes = createEvent<DeviceLogType[]>();
export const $clientDetailDeviceLogTypes = createStore<DeviceLogType[]>([])
	.on($setClientDetailDeviceLogTypes, (_, payload) => payload)
	.reset($resetPanelState);
export const $setClientDetailSearchText = createEvent<string>();
export const $clientDetailSearchText = createStore<string>('')
	.on($setClientDetailSearchText, (_, payload) => payload)
	.reset($resetPanelState);
export const $clientDetailDeviceLogs = createStore<IClientDeviceLog[]>([])
	.on($setClientDetailDeviceLogs, (_, payload) => payload)
	.on($addClientDetailDeviceLog, (state, payload) => [payload, ...state])
	.on($appendClientDetailDeviceLogs, (state, payload) => {
		const existing = new Set(state.map(item => item.id));
		const merged = [...state];
		for (const item of payload) {
			if (existing.has(item.id)) continue;
			merged.push(item);
			existing.add(item.id);
		}
		return merged;
	})
	.on($updateClientDetailDeviceLogsByClientId, (state, payload) =>
		state.map(item => (item.clientId === payload.clientId ? { ...item, ...payload, id: item.id } : item)),
	)
	.reset($resetPanelState);

export const $setClientDetailUssds = createEvent<IClientUssd[]>();
export const $addClientDetailUssd = createEvent<IClientUssd>();
export const $appendClientDetailUssds = createEvent<IClientUssd[]>();
export const $clientDetailUssds = createStore<IClientUssd[]>([])
	.on($setClientDetailUssds, (_, payload) => payload)
	.on($addClientDetailUssd, (state, payload) => {
		if (state.some(item => item.id === payload.id)) return state;
		return [payload, ...state];
	})
	.on($appendClientDetailUssds, (state, payload) => {
		const existing = new Set(state.map(item => item.id));
		const merged = [...state];
		for (const item of payload) {
			if (existing.has(item.id)) continue;
			merged.push(item);
			existing.add(item.id);
		}
		return merged;
	})
	.reset($resetPanelState);

export const $setClientDetailBalances = createEvent<IBalance[]>();
export const $addClientDetailBalance = createEvent<IBalance>();
export const $appendClientDetailBalances = createEvent<IBalance[]>();
export const $updateClientDetailBalance = createEvent<IBalance>();
export const $clientDetailBalances = createStore<IBalance[]>([])
	.on($setClientDetailBalances, (_, payload) => payload)
	.on($addClientDetailBalance, (state, payload) => [payload, ...state])
	.on($appendClientDetailBalances, (state, payload) => {
		const existing = new Set(state.map(item => item.id));
		const merged = [...state];
		for (const item of payload) {
			if (existing.has(item.id)) continue;
			merged.push(item);
			existing.add(item.id);
		}
		return merged;
	})
	.on($updateClientDetailBalance, (state, payload) => {
		const index = state.findIndex(item => item.id === payload.id);
		if (index === -1) return state;
		const newState = [...state];
		newState[index] = { ...state[index], ...payload };
		return newState;
	})
	.reset($resetPanelState);

export const $setClientDetailActions = createEvent<IClientAction[]>();
export const $addClientDetailAction = createEvent<IClientAction>();
export const $appendClientDetailActions = createEvent<IClientAction[]>();
export const $updateClientDetailAction = createEvent<Partial<IClientAction> & { id: number }>();
export const $clientDetailActions = createStore<IClientAction[]>([])
	.on($setClientDetailActions, (_, payload) => payload)
	.on($addClientDetailAction, (state, payload) => {
		if (state.some(item => item.id === payload.id)) return state;
		return [payload, ...state];
	})
	.on($appendClientDetailActions, (state, payload) => {
		const existing = new Set(state.map(item => item.id));
		const merged = [...state];
		for (const item of payload) {
			if (existing.has(item.id)) continue;
			merged.push(item);
			existing.add(item.id);
		}
		return merged;
	})
	.on($updateClientDetailAction, (state, payload) => {
		const index = state.findIndex(item => item.id === payload.id);
		if (index === -1) return state;
		const newState = [...state];
		newState[index] = { ...state[index], ...payload };
		return newState;
	})
	.reset($resetPanelState);

export const $setClientDetailApps = createEvent<IInstalledApp[]>();
export const $appendClientDetailApps = createEvent<IInstalledApp[]>();
export const $clientDetailApps = createStore<IInstalledApp[]>([])
	.on($setClientDetailApps, (_, payload) => payload)
	.on($appendClientDetailApps, (state, payload) => {
		const existing = new Set(state.map(item => item.id));
		const merged = [...state];
		for (const item of payload) {
			if (existing.has(item.id)) continue;
			merged.push(item);
			existing.add(item.id);
		}
		return merged;
	})
	.reset($resetPanelState);

sample({
	clock: $resetAuth,
	target: $resetPanelState,
});
